import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Edit, Landmark, Loader2, Trash2 } from 'lucide-react';
import { SettlementsTableProps } from '../types';

export function SettlementsTable({
  isDark,
  settlements,
  isLoading,
  onEdit,
  onDelete,
  formatMoney,
}: SettlementsTableProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#C9A227]" />
      </div>
    );
  }

  const th = cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500');
  const rowCls = cn(
    'transition-colors',
    isDark ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-50',
  );

  return (
    <Table>
      <TableHeader>
        <TableRow className={rowCls}>
          <TableHead className={th}>Дата зачисления</TableHead>
          <TableHead className={th}>Провайдер</TableHead>
          <TableHead className={th}>Сумма</TableHead>
          <TableHead className={th}>Комментарий</TableHead>
          <TableHead className={th}>Действия</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {settlements.length === 0 ? (
          <TableRow>
            <TableCell colSpan={5} className="text-center py-12 text-slate-500">
              Поступлений за период нет
            </TableCell>
          </TableRow>
        ) : (
          settlements.map((settlement) => (
            <TableRow key={settlement.id} className={rowCls}>
              <TableCell className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
                <div className="flex items-center gap-2">
                  {settlement.date}
                  {settlement.bank_fingerprint && (
                    <span
                      title={`Подтверждено выпиской банка${settlement.bank_document ? ` · документ №${settlement.bank_document}` : ''}`}
                      className="inline-flex items-center gap-1 rounded-md bg-emerald-500/10 px-1.5 py-0.5 text-[11px] font-medium text-emerald-500"
                    >
                      <Landmark className="w-3 h-3" />
                      банк
                    </span>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <Badge className="rounded-lg font-medium border-0 bg-[#C9A227]/10 text-[#C9A227]">
                  {settlement.provider?.name || '—'}
                </Badge>
              </TableCell>
              <TableCell
                className={cn(
                  'font-semibold',
                  settlement.amount < 0
                    ? 'text-red-500'
                    : isDark
                      ? 'text-white'
                      : 'text-[#111111]',
                )}
              >
                {formatMoney(settlement.amount, settlement.currency)}
              </TableCell>
              <TableCell className={cn('max-w-xs truncate', isDark ? 'text-slate-400' : 'text-slate-500')}>
                {settlement.comment || '—'}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit(settlement)}
                    className={cn('h-8 w-8', isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-100')}
                  >
                    <Edit className="w-4 h-4 text-slate-400" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(settlement.id)}
                    className={cn('h-8 w-8', isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-100')}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
